/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useExportAuditLogsMutation, useGetAuditTrailQuery } from "../../../store/api/admin/adminApiSlice";
import { auditTrailColumns } from "../../../components/Shared/Table/Columns";
import { useSelector } from "react-redux";
import { API } from "../../../globals/globals";
import { urlParameterSelector } from "../../../store/filters/audit/selectors";
import { searchKeywordSelector } from "../../../store/search/selector";
import { GridSortModel } from "@mui/x-data-grid";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { errorMessage, exportInCSV } from "../../../utils/helpers";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { initialState } from "../../../store/helpers/helperInitialState";

//import { useLocation } from "react-router-dom";

export const useAuditTrailData = () => {
  const dispatch = useStoreDispatch();
  const filters = useSelector(urlParameterSelector);
  const [auditSorting, setAuditSorting] = useState("id,desc");

  const { auditTrail = initialState.pagination } = useSelector(paginationModelSelector);
  const [exportAuditLogs, exportStatus] = useExportAuditLogsMutation();

  const keyword = useSelector(searchKeywordSelector);
  filters.set("page", auditTrail.page.toString());
  filters.set("size", auditTrail.pageSize.toString());
  filters.set("sort", auditSorting);
  filters.set("search", keyword);

  const args = { filters: filters.toString() };
  const { data, isLoading, refetch } = useGetAuditTrailQuery(args, {
    pollingInterval: API.ENDPOINTS.AUDIT.pollingInterval || API.POLLING_INTERVAL,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  //handling sorting operations
  const handleGridSort = (sortModel: GridSortModel) => {
    const sortedField: string = sortModel[0].field;
    const sortedOrder: string = sortModel[0].sort?.toLowerCase() || "desc";
    const sorted: string = sortedField.concat(",", sortedOrder);
    setAuditSorting(sorted);
  };

  const exportHandler = async () => {
    try {
      const exportFilters = new URLSearchParams(filters);
      exportFilters.delete("page");
      exportFilters.delete("size");
      const response: any = await exportAuditLogs({ filters: exportFilters.toString() });
      if (response?.error) throw response?.error;
      exportInCSV(response, "AuditLog");
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    data,
    isLoading,
    auditTrailColumns,
    handleGridSort,
    exportHandler,
    exportStatus,
  };
};